import { Component, ElementRef, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { MenuItem, TreeNode } from 'primeng/api';
import { MainAppService } from 'src/modules/app-template/services/main-app.service';
import { AceEditorComponent } from 'src/modules/sm-base/components/ace-editor/ace-editor.component';
import { AceSelection } from 'src/modules/sm-base/models/ace-selection.model';
import { ButtonDefinition } from 'src/modules/sm-base/models/button-definition.model';
import { ArrayOrSingle } from 'src/modules/sm-base/shared/array-or-single.model';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { TreeNode2 } from 'src/modules/utils/misc/tree-node2.model';
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { FrontendScriptDefinition } from '../../models/frontend-script-definition.model';
import { FrontendScriptFound } from '../../models/frontend-script-found.model';
import { FrontendScriptSearchResult } from '../../models/frontend-script-search-result.model';
import { FrontendScriptsDefinition } from '../../models/frontend-scripts-definition.model';
import { FrontendFieldListItem } from 'src/modules/sm-base/models/frontend-field-list-item.model';
import { FrontendScriptPastVersion } from '../../models/frontend-script-past-version.model';
import { DhTools } from '../../models/dh-tools.model';
import { InFileError } from 'src/modules/enaio/shared/InFileError';
import { FrontendScriptNode } from '../../models/frontend-script-node.model';
import { TreeIcon } from 'src/modules/sm-base/models/tree-icon.model';
import { FrontendScriptOutlineItem } from '../../models/frontend-script-outline-item.model';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';

@Component({
  selector: 'app-frontend-script-editor',
  templateUrl: './frontend-script-editor.component.html',
  styleUrl: './frontend-script-editor.component.scss'
})
export class FrontendScriptEditorComponent extends ComponentView {

    _Utils = Utils;
    _TreeIcon = TreeIcon;
    _scripts = new FrontendScriptsDefinition();

    scriptsTree: TreeNode2[];
    scriptsTreeSelected: ArrayOrSingle<TreeNode>;
    scriptsTreeSelectedLast: ArrayOrSingle<TreeNode>;
    outlineTree: TreeNode2[];
    sel: FrontendScriptDefinition;
    code = "";
    originalCode = "";
    showIncluded = false;
    searchRegex = false;
    searchText = "";
    searchResults: FrontendScriptSearchResult[] = [];
    searchResultTabIndex = 0;
    selectedFound = 0;

    pastVersionsListItems: FrontendFieldListItem[] = [];
    selectedPastVersion1: FrontendScriptPastVersion;
    selectedPastVersion2: FrontendScriptPastVersion;
    compareVersions = true;
    sideBySide = true;
    findUsagesIcon = new TreeIcon('findUsages', 'fas fa-search', '', null, true, true);

    @Input()
    toolbarTemplate?: TemplateRef<any>;

    @Input()
    showOutline = false;
    
    @ViewChild("aceEvent")
    aceEvent: AceEditorComponent;
    @ViewChild("aceEvent", {read: ElementRef}) private editorRef: ElementRef;
    
    @Output()
    onSave = new EventEmitter<FrontendScriptDefinition[]>();

    async initParams(): Promise<boolean> {
        this.addKeyboardListener("Ctrl-S", event => {
            if (GuiUtils.isComponentVisible(this.editorRef)) {
                if (this.canSave()) {
                    GuiUtils.angularTimer(async () => this.save());
                }
                event.preventDefault();
            }
        });
        return Promise.resolve(true);
    }

    @Input()
    public set scripts(value: FrontendScriptsDefinition) {
        this._scripts = value;
        this.scriptsTreeSelected = null;
        this.scriptsTree = GuiUtils.generateTree(this._scripts.scripts, s => [...s.parentNodes, s], (item, key, index) => ({ 
            data: key[index], 
            label: key[index].name, 
            expanded: true, 
            sortText: key[index].getSortName()
        }), true, (key, node) => node.data.id == key.id);

        this.outlineTree = GuiUtils.generateTree(this._scripts.globalOutlineItems, oi => [...oi.parents, oi], (item, key, index) => ({
            data: key[index], 
            label: key[index] instanceof FrontendScriptOutlineItem ? key[index].name : key[index], 
            expanded: true, 
            sortText: key[index] instanceof FrontendScriptOutlineItem ? key[index].getSortName() : null
        }), true, (key, node) => node.data.id == key.id);
    }

    canSave(): boolean {
        return !this.showIncluded && this.code != this.originalCode;
    }

    getNodeIcons(node: any): TreeIcon[] {
        let result = node.data instanceof FrontendScriptDefinition ? [...(node.data as FrontendScriptDefinition).icons] : [];
        if (this.searchResultTabIndex < this.searchResults.length) {
            let sr = this.searchResults[this.searchResultTabIndex];
            let count = sr.found.filter(ser => ser.scriptId == node.data.id).length;
            if (count > 0) {
                result = [...result, new TreeIcon("", "", "", count)];
            }
        }
        return result;
    }

    getOutlineNodeIcons(node: any): TreeIcon[] {
       return node.data instanceof FrontendScriptOutlineItem ? [this.findUsagesIcon] : [];
    }

    isSaved(): boolean {
        return this.aceEvent == null || this.sel?.code == this.aceEvent.text;
    }    

    async validate(language: string, text: string): Promise<InFileError[]> {
        return language == "javascript" || language == "vbscript" ? await DhTools.backendCall("api/tools/validateFile", { language, text }).list(InFileError) : [];
    }

    async selectScript(toSelect?: FrontendScriptDefinition): Promise<void> {
        if (this.sel != null && this.canSave() && !this.isSaved()) {
            switch (await this.app.messageDialog.show("Die Änderungen wurden noch nicht gespeichert. Wie soll vorgegangen werden?", "Warnung",
            [this.app.messageDialog.save, this.app.messageDialog.cancel, new ButtonDefinition("discard", "Verwerfen", false, "")], { closeOption: "cancel" })) {
                case "save":
                    this.save();
                    break;
                case "cancel":
                    this.scriptsTreeSelected = this.scriptsTreeSelectedLast;
                    return;
            }
        }

        this.scriptsTreeSelectedLast = this.scriptsTreeSelected;
        if (toSelect != null) {
            this.scriptsTreeSelected = GuiUtils.flattenTree(this.scriptsTree).find(node => (node.data as FrontendScriptDefinition).id == toSelect.id);    
        }
        this.sel = toSelect ?? (this.scriptsTreeSelected as TreeNode)?.data as FrontendScriptDefinition;
        if (this.sel instanceof FrontendScriptNode) {
            this.sel = null;
        }

        if (this.sel != null) {
            if (this.aceEvent != null) {
                this.aceEvent.mode = this.sel.language;
            }
            this.updatePastVersionsListItems();
        }
        
        this.calculateCode();
        this.originalCode = this.code;
        GuiUtils.angularTimer(() => {
            this.aceEvent?.focus();
        });

        this.selectedPastVersion1 = null;
        this.selectedPastVersion2 = null;
    }

    updatePastVersionsListItems(): void {
        this.pastVersionsListItems = [new FrontendFieldListItem(null, "Diese Version"), ...this.sel.pastVersions != null ? Utils.arraySortBy(this.sel.pastVersions, v => v.getSortName(), false).map(v => new FrontendFieldListItem(v, v.name)) : []];
    }

    updateCode(): void {
        if (this.sel != null) {
            this.sel.code = Utils.getSubstringTo(this.aceEvent.text, "\n\n'!!!!!!!!!!!!!!!!!!!! INKLUDIERTES SKRIPT:");
        }
    }

    clickOutlineItem(icon: TreeIcon, item: FrontendScriptOutlineItem): void {
        let regex = Utils.stringDef(item.searchRegex, item.name);
        this.searchForText(regex, true,  item.name);
    }

    async syntaxCheck(showMessageGood = true, showMessageBad = true): Promise<InFileError[]> {
        this.updateCode();
        let result = await this.aceEvent.validate();
        if (result.length == 0 && showMessageGood) {
            this.app.showToast("success", "Keine Fehler", "Es wurden keine Syntaxfehler gefunden");
        }
        if (result.length > 0 && showMessageBad) {
            await this.app.messageDialog.info("Es wurden Syntaxfehler gefunden:<br><br>" + Utils.arrayItemsToString(result, "<br>", error => error.lineNumber + ": " + error.message));
        }
        return result;
    }

    async save(): Promise<void> {
        this.updateCode();
        if ((await this.syntaxCheck(false)).length == 0) {
            this.onSave.emit([this.sel]);
            this.app.showToast("success", "Gespeichert", "Die Änderungen wurden gespeichert");
            this.originalCode = this.code;
        }
    }

    calculateCode(): void {
        this.code = this.sel?.code ?? "";
        if (this.showIncluded) {
            for (let inc of this.sel?.includes) {
                let script = this._scripts.scripts.find(script2 => script2.id == inc);
                if (script != null) {
                    this.aceEvent.addDivider(Utils.splitLines(this.code).length + 1);
                    this.code += "\n\n" + (script.language == "javascript" ? "//" : "'") + "!!!!!!!!!!!!!!!!!!!! INKLUDIERTES SKRIPT: " + script.name + "\n\n" + script.code;
                }
            }
        }
        else {
            this.aceEvent.addDivider(null);
        }
    }

    search(event: any): void {
        if (event == null || event.keyCode == 13) {
            this.searchForText(this.searchText, this.searchRegex);
        }
    }

    searchForText(text: string, regex: boolean, title?: string): void {
        this.updateCode();
        let result = new FrontendScriptSearchResult(Utils.stringDef(title, Utils.crop(text, 20, true)));
        for (let script of this._scripts.scripts) {
            result.found = [...result.found, ...this.findOccurrencesInText(script.code, text, script.id, regex)];
        }
        result.createTable(this._scripts.scripts);
        this.searchResults = [...this.searchResults, result];
        this.searchText = "";
        GuiUtils.angularTimer(() => {
            this.searchResultTabIndex = this.searchResults.length - 1;
        });
    }

    async goToDeclaration(name: string): Promise<void> {
        let rx = new RegExp("\\s*((public|private)\\s+)?(function|sub)\\s+" + name, "gi");
        let index = 0;
        for (let line of Utils.splitLines(this.code)) {
            if (rx.exec(line) != null) {
                this.aceEvent.jumpToLine(index + 1, true);
                break;
            }
            index++;
        }
        if (!this.showIncluded) {
            for (let inc of this.sel.includes) {
                let script = this._scripts.scripts.find(script2 => script2.id == inc);
                if (script != null) {
                    index = 0;
                    for (let line of Utils.splitLines(script.code)) {
                        if (rx.exec(line) != null) {
                            this.scriptsTreeSelected = GuiUtils.flattenTree(this.scriptsTree).find(node => node.data == script);
                            await this.selectScript();
                            GuiUtils.angularTimer(() => {
                                this.aceEvent.jumpToLine(index + 1, true);
                            });
                            break;
                        }
                        index++;
                    }
                }
            }
        }
    }

    findOccurrencesInText(code: string, query: string, scriptId: string, regex: boolean): FrontendScriptFound[] {
        const results: FrontendScriptFound[] = [];
        const lines = code.split('\n');
        let searchPattern = new RegExp(regex ? query : query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), "gi");

        lines.forEach((line, index) => {
            let match: any;
            while ((match = searchPattern.exec(line)) !== null) {
                // Erstellen Sie den hervorgehobenen HTML-Text
                let start: number = match.index;
                let end: number = match.index + match[0].length;
                let l = line.substring(0, start) + "<span class=\"code-highlight\">" + line.substring(start, end) + "</span>" + line.substring(end);
                results.push(new FrontendScriptFound(scriptId, index + 1, l,  { ... match.groups } as OrdinaryObject));
            }
        });

        return results;
    }

    closeSearchResultsTab(event: any): void {
        this.searchResults.splice(event.inpdex - 1, 1);
        GuiUtils.angularTimer(() => {
            this.searchResultTabIndex = 0;
        });
    }

    async selectFound(event: any): Promise<void> {
        let f = event.raw as FrontendScriptFound;
        let script = this._scripts.scripts.find(s => s.id == f.scriptId);
        this.scriptsTreeSelected = GuiUtils.flattenTree(this.scriptsTree).find(node => node.data == script);
        await this.selectScript();
        GuiUtils.angularTimer(() => {
            this.aceEvent.jumpToLine(f.lineNumber, true);
        });
    }

    contextMenu(sel: AceSelection): void {
        let menu: MenuItem[] = [
            {
                label: "Suche nach " + Utils.crop(Utils.stringDef(sel.selection, sel.token), 30, true),
                icon: "fas fa-search",
                command: _ => {
                    this.searchText = Utils.stringDef(sel.selection, sel.token);
                    this.search(null);
                }
            },
            {
                label: "Finde Deklaration " + Utils.crop(Utils.stringDef(sel.selection, sel.token), 30, true),
                icon: "fas fa-search",
                command: async _ => {
                    await this.goToDeclaration(Utils.stringDef(sel.selection, sel.token));
                }
            }
        ];
        this.app.openContextMenu(menu);
    }

    async clickToken(sel: AceSelection): Promise<void> {
        await this.goToDeclaration(sel.token);
    }
}
